import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { useClientUser } from '@/hooks';
import { QUERY_BY_LEADER } from '@/graphql/queries/teammates';
import {
    CYCLE_COUNT_SHIPPER_INVENTORY_OPTIONS,
    CYCLE_COUNT_TEMPLATES,
    CYCLE_COUNT_WAREHOUSES,
} from '../graphql/queries';
import {
    INSERT_CYCLE_COUNT,
    INSERT_CYCLE_COUNT_TEMPLATE,
    UPDATE_CYCLE_COUNT_TEMPLATE,
} from '../graphql/mutations';
import {
    calendarDateToIsoString,
    deriveShipperOptions,
    getWarehouseLocationOptions,
    validateCycleCountCreate,
} from '../utils';
import { ASSIGNABLE_CYCLE_COUNT_ROLES, COUNT_MODES } from '../constants';
import LoadTemplateModal from './LoadTemplateModal';
import SaveTemplateModal from './SaveTemplateModal';

const TAG_STYLE = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1 1 auto',
    minWidth: 0,
    paddingLeft: 6,
};

// Shared render props for the multi-select Autocompletes (bins and shippers).
const renderCheckboxOption = (getLabel) => (option, { selected }) => (
    <>
        <Checkbox
            color="primary"
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
        />
        {getLabel(option)}
    </>
);

const renderSelectedCountTags = (getLabel, noun) => (value) => {
    if (value.length === 0) return null;
    const label = value.length === 1 ? getLabel(value[0]) : `${value.length} ${noun} selected`;
    return <span style={TAG_STYLE}>{label}</span>;
};

export default function CreateCycleCountModal({ open, onClose, onCreated, onNotify }) {
    const { client_id: clientId } = useClientUser();

    const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [countMode, setCountMode] = useState(COUNT_MODES.ENTIRE_WAREHOUSE);
    const [selectedBins, setSelectedBins] = useState([]);
    const [selectedShipperIds, setSelectedShipperIds] = useState([]);
    const [selectedTeammateId, setSelectedTeammateId] = useState(null);
    const [loadedTemplate, setLoadedTemplate] = useState(null);
    const [validationMessage, setValidationMessage] = useState('');
    const [showLoadTemplate, setShowLoadTemplate] = useState(false);
    const [showSaveTemplate, setShowSaveTemplate] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSelectedWarehouseId(null);
        setSelectedDate(new Date());
        setCountMode(COUNT_MODES.ENTIRE_WAREHOUSE);
        setSelectedBins([]);
        setSelectedShipperIds([]);
        setSelectedTeammateId(null);
        setLoadedTemplate(null);
        setValidationMessage('');
        setShowLoadTemplate(false);
        setShowSaveTemplate(false);
    }, [open]);

    const notifyError = (message) => onNotify?.({ severity: 'error', message });
    const notifySuccess = (message) => onNotify?.({ severity: 'success', message });

    const { data: warehousesData, loading: warehousesLoading } = useQuery(CYCLE_COUNT_WAREHOUSES, {
        variables: { client_id: clientId },
        skip: !open || !clientId,
        onError: (error) => {
            captureException(error);
            notifyError('Unable to load warehouses.');
        },
    });
    const warehouses = warehousesData?.locations || [];

    const {
        data: templatesData,
        loading: templatesLoading,
        refetch: refetchTemplates,
    } = useQuery(CYCLE_COUNT_TEMPLATES, {
        variables: { client_id: clientId, warehouse_id: selectedWarehouseId },
        skip: !open || !clientId || !selectedWarehouseId,
        onError: (error) => {
            captureException(error);
            notifyError('Unable to load templates.');
        },
    });
    const templates = templatesData?.cycle_count_templates || [];

    const { data: shipperInventoryData, loading: shippersLoading } = useQuery(
        CYCLE_COUNT_SHIPPER_INVENTORY_OPTIONS,
        {
            variables: { warehouse_id: selectedWarehouseId },
            skip: !open || !selectedWarehouseId,
            onError: (error) => {
                captureException(error);
                notifyError('Unable to load shippers.');
            },
        }
    );

    const { data: teammatesData, loading: teammatesLoading } = useQuery(QUERY_BY_LEADER, {
        variables: { leader_id: clientId },
        skip: !open || !clientId,
        onError: (error) => {
            captureException(error);
            notifyError('Unable to load teammates.');
        },
    });

    const [insertCycleCount, { loading: creating }] = useMutation(INSERT_CYCLE_COUNT, {
        onError: (error) => {
            captureException(error);
            notifyError('Unable to create cycle count.');
        },
    });
    const [insertTemplate, { loading: insertingTemplate }] = useMutation(INSERT_CYCLE_COUNT_TEMPLATE, {
        onError: (error) => {
            captureException(error);
            notifyError('Unable to save template.');
        },
    });
    const [updateTemplate, { loading: updatingTemplate }] = useMutation(UPDATE_CYCLE_COUNT_TEMPLATE, {
        onError: (error) => {
            captureException(error);
            notifyError('Unable to update template.');
        },
    });
    const savingTemplate = insertingTemplate || updatingTemplate;

    useEffect(() => {
        setSelectedBins([]);
        setSelectedShipperIds([]);
        setLoadedTemplate(null);
        setValidationMessage('');
    }, [selectedWarehouseId]);

    useEffect(() => {
        setValidationMessage('');
    }, [countMode, selectedBins, selectedShipperIds, selectedDate]);

    const selectedWarehouse = warehouses.find((w) => w.location_id === selectedWarehouseId);
    const warehouseLocationOptions = useMemo(
        () => getWarehouseLocationOptions(selectedWarehouse?.warehouse_racks),
        [selectedWarehouse?.warehouse_racks]
    );

    const assignableTeammateOptions = useMemo(
        () =>
            (teammatesData?.results || [])
                .filter((teammate) => ASSIGNABLE_CYCLE_COUNT_ROLES.some((role) => teammate.roles?.[role]))
                .map((teammate) => ({ value: teammate.teammate_id, label: teammate.username }))
                .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        [teammatesData?.results]
    );

    const { shipperOptions, locationsByShipperId } = useMemo(
        () => deriveShipperOptions(shipperInventoryData?.items),
        [shipperInventoryData?.items]
    );

    const shipperLabel = (value) => shipperOptions.find((o) => o.value === value)?.label || value;

    const selectedShipperLocations = useMemo(() => {
        const set = new Set();
        selectedShipperIds.forEach((shipperId) => {
            locationsByShipperId.get(shipperId)?.forEach((loc) => set.add(loc));
        });
        return Array.from(set).sort();
    }, [locationsByShipperId, selectedShipperIds]);

    const resolvedWarehouseLocations = useMemo(() => {
        if (countMode === COUNT_MODES.ENTIRE_WAREHOUSE) {
            return warehouseLocationOptions.map((o) => o.value);
        }
        if (countMode === COUNT_MODES.SPECIFIC_BINS) return selectedBins;
        return selectedShipperLocations;
    }, [countMode, selectedBins, selectedShipperLocations, warehouseLocationOptions]);

    const binValueSet = useMemo(
        () => new Set(warehouseLocationOptions.map((o) => o.value)),
        [warehouseLocationOptions]
    );

    const onCreate = async () => {
        const error = validateCycleCountCreate({
            warehouseId: selectedWarehouseId,
            date: selectedDate,
            countMode,
            selectedBins,
            selectedShipperIds,
            resolvedLocations: resolvedWarehouseLocations,
        });
        if (error) {
            setValidationMessage(error);
            return;
        }

        const result = await insertCycleCount({
            variables: {
                object: {
                    warehouse_id: selectedWarehouseId,
                    client_id: clientId,
                    assigned_teammate_id: selectedTeammateId || null,
                    ...(countMode === COUNT_MODES.SPECIFIC_BINS && loadedTemplate?.cycle_count_template_id
                        ? { template_id: loadedTemplate.cycle_count_template_id }
                        : {}),
                    status: 'NEW',
                    date: calendarDateToIsoString(selectedDate),
                    count_locations: {
                        data: resolvedWarehouseLocations.map((warehouse_location) => ({
                            warehouse_location,
                        })),
                    },
                    ...(countMode === COUNT_MODES.SPECIFIC_SHIPPERS
                        ? {
                              shippers: {
                                  data: selectedShipperIds.map((shipper_id) => ({ shipper_id })),
                              },
                          }
                        : {}),
                },
            },
        }).catch(() => null);

        if (!result?.data?.insert_cycle_counts_one) return;

        notifySuccess('Cycle count created.');
        onCreated?.();
        onClose?.();
    };

    const onOpenLoadTemplate = () => {
        if (!selectedWarehouseId) {
            setValidationMessage('Select a warehouse.');
            return;
        }
        setValidationMessage('');
        refetchTemplates?.().catch(() => {});
        setShowLoadTemplate(true);
    };

    const onOpenSaveTemplate = () => {
        if (!selectedWarehouseId) {
            setValidationMessage('Select a warehouse.');
            return;
        }
        if (selectedBins.length === 0) {
            setValidationMessage('Select at least one bin or pool.');
            return;
        }
        setValidationMessage('');
        setShowSaveTemplate(true);
    };

    const onLoadTemplate = (template) => {
        if (!template) return;
        setLoadedTemplate(template);
        setSelectedBins(Array.isArray(template.warehouse_locations) ? template.warehouse_locations : []);
        setSelectedShipperIds([]);
        setCountMode(COUNT_MODES.SPECIFIC_BINS);
        setValidationMessage('');
        setShowLoadTemplate(false);
        notifySuccess('Template loaded.');
    };

    const onSaveTemplate = async ({ name, overwrite }) => {
        const result =
            overwrite && loadedTemplate?.cycle_count_template_id
                ? await updateTemplate({
                      variables: {
                          cycle_count_template_id: loadedTemplate.cycle_count_template_id,
                          _set: { name, warehouse_locations: selectedBins },
                      },
                  }).catch(() => null)
                : await insertTemplate({
                      variables: {
                          object: {
                              warehouse_id: selectedWarehouseId,
                              client_id: clientId,
                              name,
                              warehouse_locations: selectedBins,
                          },
                      },
                  }).catch(() => null);

        const saved =
            result?.data?.update_cycle_count_templates_by_pk ||
            result?.data?.insert_cycle_count_templates_one;
        if (!saved) return;

        setLoadedTemplate(saved);
        setSelectedBins(Array.isArray(saved.warehouse_locations) ? saved.warehouse_locations : []);
        setShowSaveTemplate(false);
        refetchTemplates?.().catch(() => {});
        notifySuccess(overwrite ? 'Template overwritten.' : 'Template saved.');
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>New Cycle Count</DialogTitle>
                <DialogContent>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <TextField
                                select
                                fullWidth
                                variant="outlined"
                                label={warehousesLoading ? 'Loading warehouses...' : 'Warehouse'}
                                value={selectedWarehouseId || ''}
                                onChange={(e) => setSelectedWarehouseId(e.target.value || null)}
                                disabled={warehousesLoading}
                            >
                                {warehouses.map((warehouse) => (
                                    <MenuItem key={warehouse.location_id} value={warehouse.location_id}>
                                        {warehouse.location_name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Date"
                                    value={selectedDate}
                                    onChange={(value) => setSelectedDate(value)}
                                    slotProps={{
                                        textField: { fullWidth: true, variant: 'outlined' },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item>
                            <FormControl component="fieldset" fullWidth>
                                <RadioGroup
                                    value={countMode}
                                    onChange={(_, value) => setCountMode(value)}
                                >
                                    <FormControlLabel
                                        value={COUNT_MODES.ENTIRE_WAREHOUSE}
                                        control={<Radio color="primary" />}
                                        label="Entire Warehouse"
                                    />
                                    <FormControlLabel
                                        value={COUNT_MODES.SPECIFIC_BINS}
                                        control={<Radio color="primary" />}
                                        label="Specific Bins"
                                    />
                                    <FormControlLabel
                                        value={COUNT_MODES.SPECIFIC_SHIPPERS}
                                        control={<Radio color="primary" />}
                                        label="Specific Shippers"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        {countMode === COUNT_MODES.SPECIFIC_BINS ? (
                            <Grid item>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    fullWidth
                                    options={warehouseLocationOptions.map((o) => o.value)}
                                    value={selectedBins.filter((b) => binValueSet.has(b))}
                                    onChange={(_, value) => setSelectedBins(value)}
                                    disabled={!selectedWarehouseId}
                                    renderOption={renderCheckboxOption((option) => option)}
                                    renderTags={renderSelectedCountTags((v) => v, 'locations')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            variant="outlined"
                                            label="Bins / Pools"
                                            placeholder={
                                                selectedWarehouseId
                                                    ? 'Select bins / pools'
                                                    : 'Select a warehouse first'
                                            }
                                        />
                                    )}
                                />
                                {loadedTemplate ? (
                                    <Typography
                                        css={css`
                                            margin-top: 0.5rem;
                                            color: #5f5f5f;
                                            font-size: 13px;
                                        `}
                                    >
                                        Loaded template: {loadedTemplate.name}
                                    </Typography>
                                ) : null}
                                <div
                                    css={css`
                                        display: flex;
                                        gap: 0.5rem;
                                        margin-top: 0.75rem;
                                    `}
                                >
                                    <SecondaryButton onClick={onOpenLoadTemplate}>
                                        Load Template
                                    </SecondaryButton>
                                    <SecondaryButton onClick={onOpenSaveTemplate}>
                                        Save Template
                                    </SecondaryButton>
                                </div>
                            </Grid>
                        ) : null}
                        {countMode === COUNT_MODES.SPECIFIC_SHIPPERS ? (
                            <Grid item>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    fullWidth
                                    options={shipperOptions.map((o) => o.value)}
                                    value={selectedShipperIds}
                                    onChange={(_, value) => setSelectedShipperIds(value)}
                                    disabled={!selectedWarehouseId || shippersLoading}
                                    loading={shippersLoading}
                                    getOptionLabel={shipperLabel}
                                    renderOption={renderCheckboxOption(shipperLabel)}
                                    renderTags={renderSelectedCountTags(shipperLabel, 'shippers')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            variant="outlined"
                                            label="Shippers"
                                            placeholder={
                                                !selectedWarehouseId
                                                    ? 'Select a warehouse first'
                                                    : shippersLoading
                                                      ? 'Loading shippers...'
                                                      : 'Select shippers'
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        ) : null}
                        <Grid item>
                            <TextField
                                select
                                fullWidth
                                variant="outlined"
                                label={teammatesLoading ? 'Loading teammates...' : 'Assign To'}
                                value={selectedTeammateId || ''}
                                onChange={(e) => setSelectedTeammateId(e.target.value || null)}
                                disabled={teammatesLoading}
                            >
                                <MenuItem value="">
                                    <em>Unassigned</em>
                                </MenuItem>
                                {assignableTeammateOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {validationMessage ? (
                            <Grid item>
                                <Typography
                                    css={css`
                                        color: ${colors.reds.cancelled};
                                        text-align: center;
                                        font-weight: 600;
                                    `}
                                >
                                    {validationMessage}
                                </Typography>
                            </Grid>
                        ) : null}
                    </Grid>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'space-between', padding: '8px 24px 16px' }}>
                    <SecondaryButton onClick={onClose} disabled={creating}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={onCreate} disabled={creating} loading={creating}>
                        Create Cycle Count
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            <LoadTemplateModal
                open={showLoadTemplate}
                templates={templates}
                loadedTemplate={loadedTemplate}
                loading={templatesLoading}
                onCancel={() => setShowLoadTemplate(false)}
                onLoad={onLoadTemplate}
            />
            <SaveTemplateModal
                open={showSaveTemplate}
                loadedTemplate={loadedTemplate}
                saving={savingTemplate}
                onCancel={() => setShowSaveTemplate(false)}
                onSave={onSaveTemplate}
            />
        </>
    );
}
